/**
 * API service for communicating with SiteGPT backend
 */

import type { BotConfig, Message, LeadData } from '../types/config';

const DEFAULT_API_URL = 'https://grandiose-lyrebird-292.convex.site';
// const DEFAULT_API_URL = process.env.API_BASE_URL || 'https://example.com';

export class ApiService {
  private baseUrl: string;
  private botId: string;
  private debug: boolean;

  constructor(botId: string, apiBaseUrl?: string, debug = false) {
    this.botId = botId;
    this.baseUrl = apiBaseUrl || DEFAULT_API_URL;
    this.debug = debug;
  }

  private log(...args: unknown[]): void {
    if (this.debug) {
      console.log('[SiteGPT]', ...args);
    }
  }

  /**
   * Fetch bot configuration and appearance
   */
  async getBotConfig(): Promise<BotConfig> {
    this.log('Fetching bot config...');

    const response = await fetch(
      `${this.baseUrl}/api/bot/config?botId=${encodeURIComponent(this.botId)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to fetch bot config: ${response.status}`);
    }

    const config = await response.json();
    this.log('Bot config received:', config);
    return config;
  }

  /**
   * Create a lead
   */
  async createLead(data: LeadData): Promise<string> {
    this.log('Creating lead...', data);

    const response = await fetch(`${this.baseUrl}/api/lead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        botId: this.botId,
        name: data.name,
        email: data.email,
        phone: data.phone,
        message: 'Widget lead capture',
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to create lead: ${response.status}`);
    }

    const result = await response.json();
    this.log('Lead created:', result.leadId);
    return result.leadId;
  }

  /**
   * Check if a conversation exists on the server
   * Returns true if exists, false if not found or error
   */
  async checkConversationExists(conversationId: string): Promise<boolean> {
    this.log('Checking if conversation exists...', conversationId);

    try {
      const response = await fetch(
        `${this.baseUrl}/api/conversation/messages?conversationId=${encodeURIComponent(conversationId)}&limit=1`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 404) {
        this.log('Conversation not found on server');
        return false;
      }

      if (!response.ok) {
        this.log('Error checking conversation:', response.status);
        return false;
      }

      this.log('Conversation exists on server');
      return true;
    } catch (error) {
      this.log('Error checking conversation:', error);
      return false;
    }
  }

  /**
   * Get conversation messages (for restoring history)
   */
  async getConversationMessages(conversationId: string, limit = 50): Promise<Message[]> {
    this.log('Fetching conversation messages...', conversationId);

    const response = await fetch(
      `${this.baseUrl}/api/conversation/messages?conversationId=${encodeURIComponent(conversationId)}&limit=${limit}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Failed to fetch messages: ${response.status}`);
    }

    const result = await response.json();
    this.log('Messages received:', result.messages.length);
    return result.messages;
  }

  /**
   * Send a chat message with streaming response
   */
  async sendMessageStreaming(
    message: string,
    conversationId: string | null,
    leadId: string | null,
    onChunk: (text: string) => void,
    onComplete: (fullResponse: string, newConversationId: string) => void,
    onError: (error: Error) => void
  ): Promise<void> {
    this.log('Sending message (streaming)...', { message, conversationId });

    try {
      const response = await fetch(`${this.baseUrl}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          botId: this.botId,
          conversationId,
          message,
          leadId,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(error.error || `Chat request failed: ${response.status}`);
      }

      // Get conversation ID from header
      // IMPORTANT: Server must set Access-Control-Expose-Headers for browser to read this
      const newConvId = response.headers.get('X-Conversation-Id') || conversationId || '';
      this.log('Received conversation ID from header:', newConvId);

      // Handle streaming response
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      if (!reader) {
        throw new Error('No response body');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        fullResponse += chunk;
        onChunk(chunk);
      }

      this.log('Streaming complete:', { fullResponse: fullResponse.substring(0, 100) });
      onComplete(fullResponse, newConvId);
    } catch (error) {
      this.log('Streaming error:', error);
      onError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /**
   * Send a chat message with synchronous response (fallback)
   */
  async sendMessageSync(
    message: string,
    conversationId: string | null,
    leadId: string | null
  ): Promise<{ response: string; conversationId: string }> {
    this.log('Sending message (sync)...', { message, conversationId });

    const response = await fetch(`${this.baseUrl}/api/chat/sync`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        botId: this.botId,
        conversationId,
        message,
        leadId,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `Chat request failed: ${response.status}`);
    }

    const result = await response.json();
    this.log('Sync response received:', result);

    return {
      response: result.response,
      conversationId: result.conversationId,
    };
  }
}
